/**
 * NotificationPanel — the dropdown panel that renders when the bell is opened.
 *
 * Opening the panel is read-only. Tapping a row triggers markRead (that row
 * flips to read and clears its matching badge count) via NotificationRow.
 *
 * Newest-first ordering is preserved as delivered by the server.
 */

import { useEffect } from "react";
import { Link } from "react-router-dom";
import type { UseNotificationsResult } from "../../hooks/useNotifications";
import { NotificationRow } from "./NotificationRow";

interface NotificationPanelProps {
  notifs: UseNotificationsResult;
  /** Called when the panel should close (e.g. clicking outside). */
  onClose: () => void;
}

export function NotificationPanel({ notifs, onClose }: NotificationPanelProps) {
  const { status, items, hasMore, loadMore, markRead } = notifs;

  // Close on Escape.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="notif-panel"
      role="dialog"
      aria-label="Notifications"
      aria-modal="false"
    >
      {/* Panel header */}
      <div className="notif-panel-header">
        <h2 className="notif-panel-title">Notifications</h2>
        <button
          type="button"
          className="notif-panel-close"
          onClick={onClose}
          aria-label="Close notifications"
        >
          ✕
        </button>
      </div>

      {/* Panel body */}
      <div
        className="notif-panel-body"
        role="list"
        aria-label="Notification list"
      >
        {status === "loading" && (
          <div className="notif-panel-empty">
            <span className="notif-panel-empty-text">Loading…</span>
          </div>
        )}

        {status === "error" && (
          <div className="notif-panel-empty">
            <span className="notif-panel-empty-text">
              Could not load notifications.
            </span>
          </div>
        )}

        {status === "loaded" && items.length === 0 && (
          <div className="notif-panel-empty" data-testid="notif-empty">
            <span className="notif-panel-empty-icon" aria-hidden>
              🔔
            </span>
            <p className="notif-panel-empty-text">You're all caught up.</p>
            <p className="notif-panel-empty-sub">
              Notifications about your people and automations will appear here.
            </p>
          </div>
        )}

        {status === "loaded" &&
          items.map((item) => (
            <div key={item.id} role="listitem">
              <NotificationRow item={item} onMarkRead={markRead} />
            </div>
          ))}
      </div>

      {/* Footer — Load more (when paged) + a link to the full log. The bell is
          a quick peek; the dedicated /notifications page is the browsable log
          (SILL-414). */}
      {status === "loaded" && (
        <div className="notif-panel-footer">
          {hasMore && (
            <button
              type="button"
              className="notif-load-more"
              onClick={() => void loadMore()}
            >
              Load more
            </button>
          )}
          <Link
            to="/notifications"
            className="notif-view-all"
            onClick={onClose}
          >
            View all
          </Link>
        </div>
      )}
    </div>
  );
}
